import React from "react";
import {
  Box,
  Card,
  Typography,
  Avatar,
  IconButton,
  styled,
} from "@mui/material";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import BarChartIcon from "@mui/icons-material/BarChart";
import GroupIcon from "@mui/icons-material/Group";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { Game } from "../../types/ScheduleTypes";
import { useLogger } from "../../contexts/LoggerContext";
import { useNavigate } from "react-router-dom";
import { didClubWin } from "../../helpers/helpers";

// Greys for lighter tones
const HOME_BG = "rgba(123, 45, 58, 0.9)";
const HOME_BORDER = "rgba(255, 255, 255, 0.54)";
const AWAY_BG = "rgba(255, 255, 255, 0.65)";
const AWAY_BORDER = "rgba(255, 255, 255, 0.54)";
const HOME_TEXT = "rgba(255, 255, 255, 0.9)";
const AWAY_TEXT = "rgba(0, 0, 0, 0.9)";

const StyledCard = styled(Card)<{ ishome: string }>(({ ishome }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: 16,
  borderLeft: `8px solid ${ishome === "true" ? HOME_BORDER : AWAY_BORDER}`,
  background: ishome === "true" ? HOME_BG : AWAY_BG,
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
}));

interface ScheduleCardProps {
  game: Game;
  clubId: number;
}

export const ScheduleCard: React.FC<ScheduleCardProps> = ({ game, clubId }) => {
  const isHome = game.home_club.id === clubId;
  const opponent = isHome ? game.away_club : game.home_club;
  const clubScore = isHome ? game.home_score : game.away_score;
  const opponentScore = isHome ? game.away_score : game.home_score;
  const result = didClubWin(game, clubId);
  const { setSelectedGame } = useLogger();
  const navigate = useNavigate();

  // Dummy data
  const weather = "Sunny, 75°F";
  const travelTime = "1h 45m";

  // Use opponent.name for display
  const opponentDisplayName = opponent.name.replace("School", "");

  const handleGameLoggerClick = () => {
    setSelectedGame(game);
    navigate("/game-logger");
  };

  return (
    <StyledCard ishome={isHome ? "true" : "false"}>
      {/* Date and Home/Away */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: 100,
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: isHome ? HOME_TEXT : AWAY_TEXT, fontWeight: 700 }}
        >
          {game.date}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: isHome ? HOME_TEXT : AWAY_TEXT }}
        >
          {isHome ? "Home" : "Away"}
        </Typography>
      </Box>
      {/* Opponent and Weather */}
      <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 2 }}>
        <Avatar
          src={opponent.profile_picture}
          alt={opponentDisplayName}
          sx={{ width: 48, height: 48, mr: 2 }}
        />
        <Box sx={{ flex: 1, width: 0 }}>
          <Typography
            sx={{
              fontWeight: 400,
              color: isHome ? HOME_TEXT : AWAY_TEXT,
              fontSize: "1.7rem",
              textAlign: "left",
            }}
          >
            {opponentDisplayName}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              gap: 1,
              mt: 0.5,
            }}
          >
            <WbSunnyIcon fontSize="medium" sx={{ color: "#fbc02d" }} />
            <Typography
              variant="body1"
              sx={{ color: isHome ? HOME_TEXT : AWAY_TEXT, fontSize: "1.1rem" }}
            >
              {weather}
            </Typography>
            {!isHome && (
              <>
                <DirectionsCarIcon
                  fontSize="medium"
                  sx={{ color: "#1976d2", ml: 2 }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: isHome ? HOME_TEXT : AWAY_TEXT,
                    fontSize: "1.1rem",
                  }}
                >
                  {travelTime} travel
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
      {/* Result and Score */}
      <Box sx={{ minWidth: 90, textAlign: "center" }}>
        <Typography
          variant="body1"
          sx={{ fontWeight: 700, color: isHome ? HOME_TEXT : AWAY_TEXT }}
        >
          {result}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: isHome ? HOME_TEXT : AWAY_TEXT }}
        >
          {clubScore !== null && opponentScore !== null
            ? `${clubScore}-${opponentScore}`
            : ""}
        </Typography>
      </Box>
      {/* Icons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: 120,
          gap: 1,
          pr: 2,
        }}
      >
        <IconButton aria-label="Game Logger" onClick={handleGameLoggerClick}>
          <SportsScoreIcon sx={{ color: isHome ? HOME_TEXT : AWAY_TEXT }} />
        </IconButton>
        <IconButton aria-label="Stats Home">
          <BarChartIcon sx={{ color: isHome ? HOME_TEXT : AWAY_TEXT }} />
        </IconButton>
        <IconButton aria-label="Depth Chart">
          <GroupIcon sx={{ color: isHome ? HOME_TEXT : AWAY_TEXT }} />
        </IconButton>
      </Box>
    </StyledCard>
  );
};
